<template>
    <div>
        <!-- 移动端卡片模式 -->
        <div v-if="cardMode" class="card bg-base-100 border border-base-200 hover:shadow-lg transition-all duration-200 hover:scale-[1.02]">
            <div class="card-body p-4">
                <div @click="settingCheckBox = true" class="flex items-center justify-between cursor-pointer">
                    <div class="flex items-center space-x-3">
                        <div class="p-2 bg-primary/10 rounded-lg">
                            <svg-icon name="settings" class="w-6 h-6 fill-primary" />
                        </div>
                        <div>
                            <div class="font-semibold text-base-content">详情显示设置</div>
                            <div class="text-sm text-base-content/60">音乐库显示配置和字段管理</div>
                        </div>
                    </div>
                    <div class="flex-shrink-0">
                        <span class="loading loading-infinity loading-md text-primary" v-if="isLoading"></span>
                        <svg v-else class="w-5 h-5 fill-current text-base-content/40 hover:text-primary transition-colors" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                        </svg>
                    </div>
                </div>
            </div>
        </div>

        <!-- 桌面端模式 -->
        <div v-else :class="['flex', 'flex-col', detail ? 'w-48 items-start' : 'w-32 items-center']">
            <div class="mt-2">
                <button @click="settingCheckBox = true"
                    :class="['btn btn-ghost btn-sm hover:bg-primary hover:text-primary-content transition-all duration-200', !detail ? 'tooltip tooltip-top' : '']"
                    data-tip="详情显示设置">
                    <span class="loading loading-infinity loading-md" v-if="isLoading"></span>
                    <span v-else :class="['flex', 'items-center', detail ? 'w-40' : '']">
                        <svg-icon name="settings" class="w-16 h-6 fill-current" />
                        <div v-if="detail" class="mr-auto font-medium">详情设置</div>
                    </span>
                </button>
            </div>
        </div>

        <!-- 专业化设置抽屉 -->
        <div class="drawer">
            <input id="batch-settings" type="checkbox" class="drawer-toggle" v-model="settingCheckBox" />
            <div class="drawer-side z-50">
                <div @click="settingCheckBox = false" aria-label="close sidebar" class="drawer-overlay"></div>
                <div class="min-h-full bg-base-100 text-base-content w-80 md:w-96 flex flex-col pb-20 md:pb-4">
                    <!-- 简化的标题栏 -->
                    <div class="flex items-center justify-between p-4 border-b border-base-200">
                        <div class="text-xl font-medium">详情显示设置</div>
                        <div>
                            <button class="btn btn-sm btn-neutral" @click="handleBatchSettings">确认</button>
                            <button class="btn btn-sm ml-1" @click="settingCheckBox = false">取消</button>
                        </div>
                    </div>

                    <!-- 设置内容区域 -->
                    <div class="flex-1 p-4 space-y-6 overflow-y-auto">
                        <!-- Media 目录设置 -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-1 h-5 bg-base-content/20 rounded-full"></div>
                                <h3 class="text-lg font-semibold text-base-content">Media 目录</h3>
                                <div class="badge badge-ghost badge-sm">本地存储</div>
                            </div>
                            
                            <div class="space-y-3 ml-4 pl-3 border-l border-base-200">
                                <div class="form-control">
                                    <label class="label cursor-pointer justify-start space-x-3 py-2">
                                        <input type="checkbox" class="toggle toggle-primary"
                                            v-model="batchSettingFormData.isOpenDetail" />
                                        <div class="flex-1">
                                            <span class="label-text font-medium">启用详情页面</span>
                                            <div class="text-xs text-base-content/60 mt-1">显示音乐文件的详细信息和元数据</div>
                                        </div>
                                    </label>
                                </div>
                                
                                <div class="form-control">
                                    <label class="label cursor-pointer justify-start space-x-3 py-2">
                                        <input type="checkbox" class="toggle toggle-primary"
                                            v-model="batchSettingFormData.isDepth" />
                                        <div class="flex-1">
                                            <span class="label-text font-medium">深度遍历子目录</span>
                                            <div class="text-xs text-base-content/60 mt-1">扫描并显示子文件夹内的音乐文件</div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- WebDAV 目录设置 -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-1 h-5 bg-base-content/30 rounded-full"></div>
                                <h3 class="text-lg font-semibold text-base-content">WebDAV 目录</h3>
                                <div class="badge badge-ghost badge-sm">网络存储</div>
                            </div>
                            
                            <div class="space-y-3 ml-4 pl-3 border-l border-base-200">
                                <div class="form-control">
                                    <label class="label cursor-pointer justify-start space-x-3 py-2">
                                        <input type="checkbox" class="toggle toggle-primary"
                                            v-model="batchSettingFormData.isOpenWebdav" />
                                        <div class="flex-1">
                                            <span class="label-text font-medium">启用详情页面</span>
                                            <div class="text-xs text-base-content/60 mt-1">显示 WebDAV 远程文件的详细信息</div>
                                        </div>
                                    </label>
                                </div>
                                
                                <div class="form-control">
                                    <label class="label cursor-pointer justify-start space-x-3 py-2">
                                        <input type="checkbox" class="toggle toggle-primary"
                                            v-model="batchSettingFormData.isDepthWebdav" />
                                        <div class="flex-1">
                                            <span class="label-text font-medium">深度遍历子目录</span>
                                            <div class="text-xs text-warning mt-1 flex items-center space-x-1">
                                                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                                </svg>
                                                <span>谨慎启用，会频繁访问网盘服务</span>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- 性能控制 -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-1 h-5 bg-base-content/20 rounded-full"></div>
                                <h3 class="text-lg font-semibold text-base-content">性能控制</h3>
                            </div>
                            
                            <div class="ml-4 pl-3 border-l border-base-200">
                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">显示条目限制</span>
                                    </label>
                                    <div class="flex items-center space-x-3">
                                        <input type="number" placeholder="30" min="1" max="1000"
                                            class="input input-bordered input-sm w-32 font-mono"
                                            v-model="batchSettingFormData.limit" />
                                        <span class="text-sm text-base-content/60">条目</span>
                                    </div>
                                    <div class="label">
                                        <span class="label-text-alt text-base-content/50">限制列表显示数量以确保页面响应性能</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 字段显示配置 -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-1 h-5 bg-primary/40 rounded-full"></div>
                                <h3 class="text-lg font-semibold text-base-content">显示字段</h3>
                                <div class="badge badge-primary badge-outline badge-sm">{{ showFieldsID3.length }} 个字段</div>
                            </div>
                            
                            <div class="ml-4 pl-3 border-l border-base-200">
                                <div class="label">
                                    <span class="label-text font-medium">选择要显示的元数据字段</span>
                                </div>
                                <div class="multi-select-container">
                                    <multi-select :options="fieldListID3" v-model="showFieldsID3"></multi-select>
                                </div>
                                <div class="label">
                                    <span class="label-text-alt text-base-content/50">自定义音乐库详情页面显示的字段内容</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import MultiSelect from "@/components/iview/MultiSelect";
import SvgIcon from '@/components/SvgIcon.vue'
export default {
    name: 'BatchSettings',
    components: { MultiSelect, SvgIcon },
    props: {
        detail: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        cardMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            settingCheckBox: false,
            batchSettingFormData: {
                'isOpenDetail': localStorage.getItem('isOpenDetail') !== 'false',
                'isDepth': localStorage.getItem('isDepth') === 'true',
                'isOpenWebdav': localStorage.getItem('isOpenWebdav') === 'true',
                'isDepthWebdav': localStorage.getItem('isDepthWebdav') === 'true',
                'limit': localStorage.getItem('limit') ? localStorage.getItem('limit') : 32
            },
            showFieldsID3: localStorage.getItem('showFieldsID3') ? JSON.parse(localStorage.getItem('showFieldsID3')) : ['album_img', 'filename', 'title', 'artist', 'album', 'albumartist', 'lyrics', 'lyrics_file', 'year', 'genre'],
            fieldListID3: [
                { id: 'checkbox', name: '勾选' },
                { id: 'album_img', name: '专辑封面' },
                { id: 'title', name: '标题' },
                { id: 'filename', name: '文件名' },
                { id: 'artist', name: '艺术家' },
                { id: 'album', name: '专辑' },
                { id: 'album_type', name: '专辑类型' },
                { id: 'albumartist', name: '专辑艺术家' },
                { id: 'discnumber', name: '光盘编号' },
                { id: 'tracknumber', name: '音轨号' },
                { id: 'totaltracks', name: '总音轨数' },
                { id: 'totaldiscs', name: '总光盘数' },
                { id: 'genre', name: '风格' },
                { id: 'year', name: '年份' },
                { id: 'lyrics', name: '歌词' },
                { id: 'lyrics_file', name: '歌词(LRC文件)' },
                { id: 'comment', name: '简介' },
                { id: 'description', name: '描述' },
                { id: 'duration', name: '时长' },
                { id: 'size', name: '文件大小' },
                { id: 'bit_rate', name: '比特率' },
                { id: 'sample_rate', name: '采样率' },
                { id: 'channels', name: '声道' },
                { id: 'bit_depth', name: '位深度' },
                { id: 'language', name: '语言' },
                { id: 'composer', name: '作曲家' },
                { id: 'lyricist', name: '作词家' },
                { id: 'replaygain_track_gain', name: '曲目增益' },
                { id: 'replaygain_track_peak', name: '曲目峰值' },
                { id: 'replaygain_album_gain', name: '专辑增益' },
                { id: 'replaygain_album_peak', name: '专辑峰值' }
            ]
        }
    },
    methods: {
        handleBatchSettings() {
            window.localStorage.setItem('isOpenDetail', this.batchSettingFormData.isOpenDetail)
            window.localStorage.setItem('isDepth', this.batchSettingFormData.isDepth)
            window.localStorage.setItem('isOpenWebdav', this.batchSettingFormData.isOpenWebdav)
            window.localStorage.setItem('isDepthWebdav', this.batchSettingFormData.isDepthWebdav)
            window.localStorage.setItem('limit', this.batchSettingFormData.limit)
            const obj = JSON.stringify(this.showFieldsID3)
            window.localStorage.setItem('showFieldsID3', obj)
            this.settingCheckBox = false
            this.$emit('settings-saved');
        },
        handleSetDetail() {
            this.$emit('update:detail', !this.detail);
        }
    }
}
</script>

<style scoped>
.icon {
    @apply fill-current w-16;
}

/* 自定义滚动条样式 */
.drawer-side .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.drawer-side .overflow-y-auto::-webkit-scrollbar-track {
    @apply bg-base-200 rounded-full;
}

.drawer-side .overflow-y-auto::-webkit-scrollbar-thumb {
    @apply bg-base-300 hover:bg-base-content/20 rounded-full transition-colors;
}

/* 设置分组标题动画效果 */
.space-y-4 > div:first-child {
    @apply transition-all duration-200;
}

.space-y-4:hover > div:first-child {
    @apply transform translate-x-1;
}

/* 表单控件悬停效果 */
.form-control .label {
    @apply transition-all duration-200;
}

.form-control:hover .label {
    @apply transform translate-x-1;
}

/* 数字输入框样式优化 */
.input[type="number"] {
    @apply font-mono text-center;
}

/* 多选组件容器样式 */
.multi-select-container {
    @apply bg-base-200/30 rounded-lg p-3 border border-base-300/50;
}

/* 移动端优化 */
@media (max-width: 768px) {
    .drawer-side > div:last-child {
        width: 100vw !important;
    }
    
    .drawer-side .p-6 {
        @apply p-4;
    }
    
    .drawer-side .space-y-8 {
        @apply space-y-6;
    }
    
    .drawer-side h2 {
        @apply text-lg;
    }
    
    .drawer-side h3 {
        @apply text-base;
    }
}

/* 暗色主题优化 */
@media (prefers-color-scheme: dark) {
    .bg-base-100\/80 {
        backdrop-filter: blur(12px) saturate(1.2);
    }
}

/* 标签指示器动画 */
.w-2.h-6.rounded-full {
    @apply transition-all duration-300;
    background: linear-gradient(135deg, currentColor 0%, currentColor 100%);
}

/* 徽章样式增强 */
.badge {
    @apply font-medium tracking-wide;
}

/* 按钮悬停效果 */
.btn:hover svg {
    @apply transform scale-110 transition-transform duration-200;
}

/* 切换开关样式增强 */
.toggle:checked {
    @apply shadow-lg;
}

.toggle:focus {
    @apply ring-2 ring-primary/20 ring-offset-2 ring-offset-base-100;
}
</style>